import { useQuery } from "@tanstack/react-query";
import React from "react";

import { QueryCacheKey } from "../../../api";
import {
  CenterAlign,
  Drawer,
  LoaderWithPerfTimings,
  Paper,
  PaperContent,
  Typography,
} from "../../../components";
import { WritebackDetailsSidebarContent } from "../../../components/WritebackDetailsSidebarContent";
import { useApiContext } from "../../../utils";

type Props = {
  writebackId: string | undefined;
  onClose: (value: string | undefined) => void;
};

export const WritebackDetailsDrawer: React.FC<React.PropsWithChildren<Props>> = ({
  writebackId,
  onClose,
}) => {
  const { apiClient } = useApiContext();

  const {
    data: writebackResponse,
    isLoading: isLoadingWriteback,
    error: errorWriteback,
  } = useQuery(
    [QueryCacheKey.WRITEBACK, writebackId],
    () => apiClient.getWriteback({ writeback_id: writebackId! }),
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: Boolean(writebackId),
    }
  );

  const writeback = writebackResponse?.data.writeback;

  const renderContent = () => {
    if (isLoadingWriteback) {
      return (
        <CenterAlign>
          <LoaderWithPerfTimings name="WritebackDetailsDrawer" size="large" />
        </CenterAlign>
      );
    }

    if (errorWriteback) {
      return (
        <CenterAlign>
          <Paper>
            <PaperContent>
              <Typography fontWeight="bold" gutterBottom>
                Error
              </Typography>
              <Typography>Unable to load export</Typography>
            </PaperContent>
          </Paper>
        </CenterAlign>
      );
    }

    if (writeback === undefined) {
      return <noscript />;
    }

    return (
      <WritebackDetailsSidebarContent writeback={writeback} onClose={() => onClose(undefined)} />
    );
  };

  return (
    <Drawer
      open={writebackId !== undefined}
      setOpen={open => {
        if (!open) {
          onClose(undefined);
        }
      }}
      closeOnClickAway={false}
    >
      {renderContent()}
    </Drawer>
  );
};
